import { ref, onScopeDispose } from "vue";

/**
 * useMediaQuery (th-z5i9t)
 *
 * The ONE reactive read of any `matchMedia` query. Returns a ref that starts at
 * the query's current `matches` value and flips live when the OS or viewport
 * changes it mid-session (a `matchMedia` `change` listener), cleaned up
 * automatically when the calling scope is disposed.
 *
 * A bare `window.matchMedia(q).matches` inside a `computed` is a NON-reactive
 * DOM read: the computed caches on its declared deps and never re-runs when the
 * OS flips, so every consumer downstream inherits the stale value. Reading the
 * query through this ref makes the flip a real dependency. Its readers are
 * `usePrefersReducedMotion` (`(prefers-reduced-motion: reduce)`) and the
 * `workspaceLayout` store's `resolvedAppTheme` (`(prefers-color-scheme: dark)`).
 *
 * SSR / jsdom safe: when `window.matchMedia` is unavailable the ref is a static
 * `false` ("query does not match") and no listener is registered — no crash.
 *
 * Call this in `setup()` (or inside an `effectScope` — a Pinia setup store's
 * body is one) so `onScopeDispose` has a scope to attach the listener cleanup to.
 *
 * @param {string} query a CSS media query string, e.g. `"(prefers-color-scheme: dark)"`.
 * @returns {import("vue").Ref<boolean>} reactive `true` while the query matches.
 */
export function useMediaQuery(query) {
  const matches = ref(false);

  // SSR / jsdom without matchMedia → report "no match", register nothing.
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return matches;
  }

  const mediaQueryList = window.matchMedia(query);
  matches.value = mediaQueryList.matches;

  function onChange(event) {
    matches.value = event.matches;
  }

  mediaQueryList.addEventListener("change", onChange);

  onScopeDispose(() => {
    mediaQueryList.removeEventListener("change", onChange);
  });

  return matches;
}
